import { useCallback, useRef } from 'react';
import { useStore } from '../store.js';

function stepLine(step: unknown): string {
  if (step === null || step === undefined) return 'null';
  if (typeof step !== 'object' || Array.isArray(step)) return JSON.stringify(step);
  const entries = Object.entries(step as Record<string, unknown>);
  if (entries.length === 0) return '{}';
  const [verb, arg] = entries[0]!;
  const argStr = typeof arg === 'string' ? arg : JSON.stringify(arg);
  return `${verb}: ${argStr}`;
}

export function EditorView() {
  const { source, doc, canonical, parseError, errors, warnings, setSource, canonicalise } =
    useStore();
  const fileInput = useRef<HTMLInputElement>(null);

  const loadFile = useCallback(
    (file: File) => {
      file.text().then((text) => setSource(text, file.name));
    },
    [setSource],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (file) loadFile(file);
    },
    [loadFile],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-ink-800 bg-ink-900 px-3 py-1.5">
        <span className="label">Source</span>
        <span className="flex-1" />
        <button
          className="mono rounded border border-ink-700 bg-ink-900 px-2.5 py-1 text-[11px] text-ink-200 hover:border-accent-500 hover:text-accent-500"
          onClick={() => fileInput.current?.click()}
        >
          Open .remedy…
        </button>
        <button
          className="mono rounded border border-ink-700 bg-ink-900 px-2.5 py-1 text-[11px] text-ink-200 hover:border-accent-500 hover:text-accent-500 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={canonicalise}
          disabled={!canonical}
        >
          Canonicalise
        </button>
        <input
          type="file"
          ref={fileInput}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) loadFile(f);
          }}
        />
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-2">
        <section
          className="overflow-auto border-r border-ink-800 p-3"
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {!source && (
            <div className="rounded border border-dashed border-ink-700 p-8 text-center text-ink-500">
              Drop a <code className="mono">.remedy</code> here or click{' '}
              <strong className="text-ink-300">Open</strong>.
            </div>
          )}
          {source && (
            <textarea
              className="mono h-full min-h-[50vh] w-full resize-none rounded border border-ink-800 bg-ink-950 p-3 text-ink-100 outline-none focus:border-accent-500"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              spellCheck={false}
            />
          )}
        </section>

        <section className="overflow-auto p-3">
          {parseError && (
            <>
              <h3 className="label mt-0 mb-2">Parse error</h3>
              <div className="mono border-l-2 border-status-deprecated bg-ink-900 p-2 text-xs">
                {parseError}
              </div>
            </>
          )}

          {doc && (
            <>
              <h3 className="label mt-0 mb-2">Identity</h3>
              <Card>
                <CardHeader name={doc.remedy?.name ?? '(no name)'} version={doc.version} />
                <p className="mt-1 text-[13px] text-ink-200">{doc.intent}</p>
                <div className="mono mt-2 text-ink-500">
                  {doc.kind} · {doc.remedy?.domain} · runtime: {doc.runtime ?? 'any'}
                </div>
              </Card>

              {doc.features && doc.features.length > 0 && (
                <>
                  <h3 className="label mt-6 mb-2">Features ({doc.features.length})</h3>
                  {doc.features.map((f) => (
                    <Card key={f.id}>
                      <CardHeader name={f.id} version={f.version} />
                      <p className="mt-1 text-[13px] text-ink-200">{f.intent}</p>
                      {Array.isArray(f.steps) && f.steps.length > 0 && (
                        <div className="mono mt-2 space-y-0.5 text-ink-500">
                          {f.steps.map((s, i) => (
                            <div key={i}>{stepLine(s)}</div>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </>
              )}

              {doc.flows && doc.flows.length > 0 && (
                <>
                  <h3 className="label mt-6 mb-2">Flows ({doc.flows.length})</h3>
                  {doc.flows.map((f) => (
                    <Card key={f.id}>
                      <CardHeader name={f.id} version={f.version} />
                      <p className="mt-1 text-[13px] text-ink-200">{f.intent}</p>
                    </Card>
                  ))}
                </>
              )}

              <h3 className="label mt-6 mb-2">
                Lint ({errors.length} · {warnings.length})
              </h3>
              {errors.length === 0 && warnings.length === 0 && (
                <div className="mono border-l-2 border-ink-700 bg-ink-900 p-2 text-xs">
                  clean
                </div>
              )}
              {errors.map((e, i) => (
                <IssueLine key={`e-${i}`} tone="error" {...e} />
              ))}
              {warnings.map((w, i) => (
                <IssueLine key={`w-${i}`} tone="warning" {...w} />
              ))}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-2 rounded border border-ink-800 bg-ink-900 px-3 py-2 text-[13px]">
      {children}
    </div>
  );
}

function CardHeader({ name, version }: { name: string; version?: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-semibold text-accent-500">{name}</span>
      {version && <span className="mono text-ink-500">v{version}</span>}
    </div>
  );
}

function IssueLine({
  tone,
  code,
  path,
  message,
}: {
  tone: 'error' | 'warning';
  code: string;
  path?: string;
  message: string;
}) {
  const color = tone === 'error' ? 'border-status-deprecated' : 'border-status-building';
  return (
    <div className={`mono my-1 border-l-2 ${color} bg-ink-900 p-2 text-xs`}>
      <div className="text-[11px] text-ink-500">
        [{code}] {path ?? '/'}
      </div>
      {message}
    </div>
  );
}
